import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { HistoryEntry, HistoryEntryKind } from '@models';

import { HistoryEntriesActions } from './history-entries.actions';
import { historyEntriesAdapter, historyEntriesFeature } from './history-entries.reducer';

const selectAllHistoryEntries = historyEntriesAdapter.getSelectors(
  historyEntriesFeature.selectHistoryEntriesState
).selectAll;

@Injectable({ providedIn: 'root' })
export class HistoryFacade {
  private store = inject(Store);

  readonly all = toSignal(this.store.select(selectAllHistoryEntries), { initialValue: [] as HistoryEntry[] });
  readonly loading = toSignal(this.store.select(historyEntriesFeature.selectLoading), { initialValue: false });
  readonly error = toSignal(this.store.select(historyEntriesFeature.selectError), { initialValue: null });

  byScope(projectId: string, scopeKey: string): Signal<HistoryEntry[]> {
    return computed(() =>
      this.all()
        .filter((e) => e.projectId === projectId && e.scopeKey === scopeKey)
        .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))
    );
  }

  loadScope(projectId: string, scopeKey: string): void {
    if (!projectId || !scopeKey) return;
    this.store.dispatch(HistoryEntriesActions.loadScope({ projectId, scopeKey }));
  }

  /** Fire-and-forget: the entry is persisted and upserted by the effect. */
  async add(
    projectId: string,
    scopeKey: string,
    text: string,
    kind: HistoryEntryKind = 'info',
    meta?: any
  ): Promise<void> {
    if (!projectId || !scopeKey) throw new Error('projectId and scopeKey are required');
    const entry = new HistoryEntry();
    entry.projectId = projectId;
    entry.scopeKey = scopeKey;
    entry.kind = kind;
    entry.text = text;
    entry.timestamp = Date.now();
    entry.meta = meta;
    this.store.dispatch(HistoryEntriesActions.addEntry({ entry }));
  }
}
